<template>
  <TitleLayout>
    <div class="space-y-6 fade-in">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
            Closing Fees
          </h1>
          <p class="text-gray-600 mt-1">Enter title and recording fees for the HUD-1.</p>
        </div>
        <button @click="openAddDialog" class="btn btn-primary">
          <font-awesome-icon icon="plus" class="mr-2" />
          Add Fee
        </button>
      </div>

      <!-- Tolerance Status Banner -->
      <ToleranceBanner :tolerance-status="toleranceStatus" />

      <!-- Fees Table -->
      <div class="card">
        <div class="card-header flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">Current Fees</h3>
          <span class="text-sm text-gray-500">{{ fees.length }} fee(s)</span>
        </div>
        <div class="overflow-x-auto">
          <table class="data-table">
            <thead>
              <tr>
                <th>HUD Line</th>
                <th>Name</th>
                <th>Amount</th>
                <th>Payee</th>
                <th>Tolerance</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="fees.length === 0">
                <td colspan="6" class="text-center py-12 text-gray-500">
                  <font-awesome-icon icon="file-invoice-dollar" class="text-4xl mb-3 text-gray-300" />
                  <p class="font-medium">No fees entered yet</p>
                  <p class="text-sm mt-1">Click "Add Fee" to get started.</p>
                </td>
              </tr>
              <tr v-for="fee in fees" :key="fee.id">
                <td class="font-mono text-sm">{{ fee.hud_line }}</td>
                <td>{{ fee.name }}</td>
                <td class="font-semibold">{{ formatCurrency(fee.amount) }}</td>
                <td class="text-gray-600">{{ fee.payee || '-' }}</td>
                <td>
                  <span
                    class="status-badge text-xs"
                    :class="getToleranceClass(fee.tolerance)"
                  >
                    {{ fee.tolerance }}
                  </span>
                </td>
                <td class="text-right">
                  <button
                    @click="openEditDialog(fee)"
                    class="text-primary-600 hover:text-primary-700 mr-3"
                    title="Edit"
                  >
                    <font-awesome-icon icon="edit" />
                  </button>
                  <button
                    @click="openDeleteConfirm(fee)"
                    class="text-danger-600 hover:text-danger-700"
                    title="Delete"
                  >
                    <font-awesome-icon icon="trash" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Navigation -->
      <div class="flex items-center justify-between">
        <router-link to="/title-info" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Title Info
        </router-link>
        <button @click="continueToNext" class="btn btn-primary">
          Continue to Payoffs
          <font-awesome-icon icon="arrow-right" class="ml-2" />
        </button>
      </div>
    </div>

    <!-- Add/Edit Fee Dialog -->
    <Dialog 
      :title="isEditMode ? 'Edit Fee' : 'Add New Fee'" 
      :isOpen="isDialogOpen" 
      @close="closeDialog"
    >
      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label class="form-label">HUD Line *</label>
          <select 
            v-model="formData.hudLine" 
            class="form-select" 
            :disabled="isEditMode"
            required
          >
            <option value="">Select HUD Line...</option>
            <optgroup label="Title Charges (1100s)">
              <option value="1101">1101 - Settlement/Closing Fee</option>
              <option value="1102">1102 - Abstract/Title Search</option>
              <option value="1103">1103 - Title Examination</option>
              <option value="1104">1104 - Title Insurance Binder</option>
              <option value="1105">1105 - Document Preparation</option>
              <option value="1106">1106 - Notary Fee</option>
              <option value="1107">1107 - Attorney Fee</option>
              <option value="1108">1108 - Title Insurance</option>
              <option value="1109">1109 - Lender's Coverage</option>
              <option value="1110">1110 - Owner's Coverage</option>
            </optgroup>
            <optgroup label="Government Recording (1200s)">
              <option value="1201">1201 - Recording Fees</option>
              <option value="1202">1202 - City/County Tax Stamps</option>
              <option value="1203">1203 - State Tax Stamps</option>
              <option value="1204">1204 - Intangible Tax</option>
              <option value="1205">1205 - Other Recording</option>
            </optgroup>
          </select>
        </div>

        <div>
          <label class="form-label">Fee Name *</label>
          <input
            v-model="formData.name"
            type="text"
            class="form-input"
            placeholder="e.g., Settlement Fee"
            required
          />
        </div>

        <div>
          <label class="form-label">Amount *</label>
          <div class="relative">
            <span class="absolute left-3 top-2 text-gray-500">$</span>
            <input
              v-model.number="formData.amount"
              type="number"
              step="0.01"
              min="0"
              class="form-input pl-7"
              placeholder="0.00"
              required
            />
          </div>
        </div>

        <div>
          <label class="form-label">Payee</label>
          <input
            v-model="formData.payee"
            type="text"
            class="form-input"
            placeholder="e.g., ABC Title Company"
          />
        </div>

        <!-- Error Message -->
        <div v-if="formError" class="tolerance-exceeded rounded-lg p-3">
          <div class="flex items-center">
            <font-awesome-icon icon="exclamation-circle" class="text-danger-600 mr-2" />
            <span class="text-sm text-danger-800">{{ formError }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            @click="closeDialog"
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            <font-awesome-icon v-if="isSubmitting" icon="spinner" class="mr-2 animate-spin" />
            {{ isEditMode ? 'Save Changes' : 'Add Fee' }}
          </button>
        </div>
      </form>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Confirmation
      :isOpen="isDeleteConfirmOpen"
      title="Delete Fee"
      :message="`Are you sure you want to delete '${deletingFee?.name || ''}'?`"
      confirmText="Delete"
      @confirm="handleConfirmDelete"
      @close="closeDeleteConfirm"
    />
  </TitleLayout>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import TitleLayout from '@/components/Layout/TitleLayout.vue'
import ToleranceBanner from '@/components/ToleranceBanner/index.vue'
import Dialog from '@/components/Dialog/index.vue'
import Confirmation from '@/components/Confirmation/index.vue'
import apiService from '@/api/apiService'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Fees',
  components: {
    TitleLayout,
    ToleranceBanner,
    Dialog,
    Confirmation,
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    const fees = computed(() => store.getters.fees)
    const toleranceStatus = computed(() => store.getters.toleranceStatus)

    // Dialog state
    const isDialogOpen = ref(false)
    const isEditMode = ref(false)
    const editingFeeId = ref(null)
    const isSubmitting = ref(false)
    const formError = ref(null)

    // Delete confirmation state
    const isDeleteConfirmOpen = ref(false)
    const deletingFee = ref(null)

    const formData = reactive({
      hudLine: '',
      name: '',
      amount: null,
      payee: '',
    })

    const resetForm = () => {
      formData.hudLine = ''
      formData.name = ''
      formData.amount = null
      formData.payee = ''
      formError.value = null
      editingFeeId.value = null
    }

    const openAddDialog = () => {
      resetForm()
      isEditMode.value = false
      isDialogOpen.value = true
    }

    const openEditDialog = (fee) => {
      formData.hudLine = fee.hud_line || fee.hudLine
      formData.name = fee.name
      formData.amount = fee.amount
      formData.payee = fee.payee || ''
      editingFeeId.value = fee.id
      formError.value = null
      isEditMode.value = true
      isDialogOpen.value = true
    }

    const closeDialog = () => {
      isDialogOpen.value = false
      resetForm()
    }

    const openDeleteConfirm = (fee) => {
      deletingFee.value = fee
      isDeleteConfirmOpen.value = true
    }

    const closeDeleteConfirm = () => {
      isDeleteConfirmOpen.value = false
      deletingFee.value = null
    }

    const fetchFees = async () => {
      try {
        store.dispatch('setLoading', { isLoading: true, message: 'Loading fees...' })
        
        const data = await apiService.getFees()
        console.log('[Fees] Fetched:', data)

        // Normalize fee data: convert camelCase to snake_case for consistency
        // Backend returns camelCase (hudLine) but frontend expects snake_case (hud_line)
        const normalizedFees = (data.fees || []).map(fee => {
          const normalized = { ...fee }
          // Normalize field names from camelCase to snake_case
          if (fee.hudLine !== undefined) {
            normalized.hud_line = fee.hudLine
            delete normalized.hudLine
          }
          return normalized
        })

        store.dispatch('setFees', normalizedFees)
        if (data.tolerance_status) {
          store.dispatch('setToleranceStatus', data.tolerance_status)
        }
      } catch (err) {
        console.error('[Fees] Failed to fetch:', err)
      } finally {
        store.dispatch('setLoading', false)
      }
    }

    const handleSubmit = async () => {
      isSubmitting.value = true
      formError.value = null

      try {
        const payload = {
          hud_line: formData.hudLine,
          name: formData.name,
          amount: formData.amount,
          payee: formData.payee,
        }

        if (isEditMode.value) {
          await apiService.updateFee(editingFeeId.value, payload)
          console.log('[Fees] Updated fee')
        } else {
          const data = await apiService.createFee(payload)
          console.log('[Fees] Created fee:', data)

          if (data.tolerance_warning) {
            formError.value = `Warning: ${data.tolerance_warning}`
          }
        }

        // Refresh fees list
        await fetchFees()
        
        // Close dialog
        closeDialog()
        
        // Sync closing status from API (backend sets fees_complete automatically)
        await syncClosingStatus()

      } catch (err) {
        console.error('[Fees] Failed to save:', err)
        formError.value = err.response?.data?.detail || 'Failed to save fee. Please try again.'
      } finally {
        isSubmitting.value = false
      }
    }

    const handleConfirmDelete = async () => {
      if (!deletingFee.value) return

      try {
        await apiService.deleteFee(deletingFee.value.id)
        await fetchFees()
        closeDeleteConfirm()
      } catch (err) {
        console.error('[Fees] Failed to delete:', err)
      }
    }

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '$0.00'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value)
    }

    const getToleranceClass = (tolerance) => {
      switch (tolerance) {
        case '0%':
          return 'status-danger'
        case '10%':
          return 'status-warning'
        default:
          return 'status-completed'
      }
    }

    const syncClosingStatus = async () => {
      try {
        const data = await apiService.getClosingStatus()
        
        // Update local store with API status (backend is source of truth)
        if (data.progress) {
          store.dispatch('setClosingProgress', {
            titleInfoComplete: data.progress.title_info_complete || false,
            feesComplete: data.progress.fees_complete || false,
            payoffsComplete: data.progress.payoffs_complete || false,
            previewGenerated: data.progress.preview_generated || false,
            documentsGenerated: data.progress.documents_generated || false,
          })
        }
      } catch (err) {
        console.error('[Fees] Failed to sync closing status:', err)
      }
    }

    const continueToNext = async () => {
      try {
        // Update closing status via PATCH to mark fees as complete
        await apiService.updateClosingStatus({
          fees_complete: true,
        })
        
        // Sync with backend to get updated status
        await syncClosingStatus()
      } catch (err) {
        console.error('[Fees] Failed to update closing status:', err)
        // Still continue even if update fails
      }
      
      router.push('/payoffs')
    }

    onMounted(() => {
      fetchFees()
    })

    return {
      fees,
      toleranceStatus,
      isDialogOpen,
      isEditMode,
      isSubmitting,
      formData,
      formError,
      isDeleteConfirmOpen,
      deletingFee,
      openAddDialog,
      openEditDialog,
      closeDialog,
      openDeleteConfirm,
      closeDeleteConfirm,
      handleSubmit,
      handleConfirmDelete,
      formatCurrency,
      getToleranceClass,
      continueToNext,
      syncClosingStatus,
    }
  },
}
</script>
